import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    CreateServiceRequest,
    ServiceRequest,
    ServiceRequestPaginate,
    ServiceRequestPopulate,
    StatusService,
} from 'app/models/serviceRequest';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ServiceRequestService {
    private baseUrl = `${environment.apiUrl}service-request`;

    constructor(private readonly _http: HttpClient) {}

    /**
     * Cria uma nova solicitação de serviço.
     *
     * @param body - Dados para criar a solicitação de serviço.
     * @returns {Observable<ServiceRequest>} - Observable contendo a solicitação de serviço criada.
     */
    createService(body: CreateServiceRequest): Observable<ServiceRequest> {
        return this._http.post<ServiceRequest>(`${this.baseUrl}`, body);
    }

    countAllStatuses(): Observable<Record<StatusService, number>> {
        return this._http.get<Record<StatusService, number>>(
            `${this.baseUrl}/count/statuses`
        );
    }

    /**
     * Atualiza uma solicitação de serviço existente.
     *
     * @param body - Dados para atualizar a solicitação de serviço.
     * @param id - ID da solicitação de serviço a ser atualizada.
     * @returns {Observable<ServiceRequest>} - Observable contendo a solicitação de serviço atualizada.
     */
    updateService(
        body: CreateServiceRequest,
        id: string
    ): Observable<ServiceRequest> {
        return this._http.patch<ServiceRequest>(`${this.baseUrl}/${id}`, body);
    }

    /**
     * Envia uma solicitação para atualizar o status de um pedido de serviço.
     *
     * @param { string } id - Identificador do pedido de serviço.
     * @param { StatusService } newStatus - Novo status para o pedido de serviço.
     * @returns { Observable<ServiceRequest> } - Observable contendo o pedido de serviço atualizado.
     */
    statusUpdate(
        id: String,
        newStatus: StatusService
    ): Observable<ServiceRequest> {
        return this._http.get<ServiceRequest>(
            `${this.baseUrl}/status-update/${id}?newStatus=${newStatus}`
        );
    }

    /**
     * Obtém uma lista paginada de serviços com base em parâmetros fornecidos, como limite, página, descrição e e-mail do solicitante.
     *
     * @param { number } limit - Número de resultados por página.
     * @param { number } page - Página atual da paginação.
     * @param { string } [description] - Descrição do serviço (opcional).
     * @param { string } [emailUserRequest] - E-mail do solicitante do serviço (opcional).
     * @returns { Observable<ServiceRequestPaginate> } - Observable contendo a resposta paginada dos serviços.
     */
    getServices(
        limit: number,
        page: number,
        description?: string,
        emailUserRequest?: string,
        serviceType?: string,
        status?: string
    ): Observable<ServiceRequestPaginate> {
        let url = `${this.baseUrl}?limit=${limit}&skip=${page}`;

        if (description) {
            url += `&description=${description.trim()}`;
        }
        if (emailUserRequest) {
            url += `&emailUserRequest=${emailUserRequest.trim()}`;
        }
        if (serviceType) {
            url += `&serviceType=${serviceType}`;
        }
        if (status) {
            url += `&status=${status}`;
        }

        return this._http.get<ServiceRequestPaginate>(url);
    }

    /**
     * Obtém uma lista paginada de serviços com base em parâmetros fornecidos, como limite, página, descrição e e-mail do solicitante.
     *
     * @param { number } limit - Número de resultados por página.
     * @param { number } page - Página atual da paginação.
     * @param { string } [description] - Descrição do serviço (opcional).
     * @param { string } [emailUserRequest] - E-mail do solicitante do serviço (opcional).
     * @returns { Observable<ServiceRequestPaginate> } - Observable contendo a resposta paginada dos serviços.
     */
    getServicesWithStatus(
        limit: number,
        page: number,
        description?: string,
        emailUserRequest?: string,
        serviceType?: string,
        status?: StatusService,
        date_start?: Date,
        date_end?: Date
    ): Observable<ServiceRequestPaginate> {
        let url = `${this.baseUrl}?limit=${limit}&skip=${page}`;

        if (description) {
            url += `&description=${description.trim()}`;
        }
        if (emailUserRequest) {
            url += `&emailUserRequest=${emailUserRequest.trim()}`;
        }
        if (serviceType) {
            url += `&serviceType=${serviceType}`;
        }
        if (status) {
            url += `&status=${status}`;
        }
        if (date_start) {
            url += `&date_start=${date_start}`;
        }
        if (date_end) {
            url += `&date_end=${date_end}`;
        }

        return this._http.get<ServiceRequestPaginate>(url);
    }

    /**
     * Obtém serviços com base em filtros aplicados e retorna os resultados paginados
     * junto com a contagem total e os status agrupados.
     *
     * @param {number} limit - Número máximo de serviços por página.
     * @param {number} page - Página atual da paginação.
     * @param {string} [description] - Descrição do serviço para filtrar.
     * @param {string} [emailUserRequest] - Email do usuário solicitante para filtrar.
     * @param {string} [serviceType] - Tipo de serviço para filtrar.
     * @param {StatusService} [status] - Status do serviço para filtrar.
     * @param {Date} [date_start] - Data de início para o filtro de criação.
     * @param {Date} [date_end] - Data de fim para o filtro de criação.
     * @returns {Observable<{services: ServiceRequest[], total: number, statusCounts?: Record<StatusService, number>}>}
     * Observable contendo os serviços, o total de serviços e os status agrupados.
     */
    getServicesWithStatusAndCount(
        limit: number,
        page: number,
        description?: string,
        emailUserRequest?: string,
        serviceType?: string,
        status?: StatusService,
        date_start?: Date,
        date_end?: Date
    ): Observable<{
        services: ServiceRequest[];
        total: number;
        statusCounts?: Record<StatusService, number>;
    }> {
        let url = `${this.baseUrl}/pagination/count/statuses?limit=${limit}&skip=${page}`;

        if (description) {
            url += `&description=${description.trim()}`;
        }
        if (emailUserRequest) {
            url += `&emailUserRequest=${emailUserRequest.trim()}`;
        }
        if (serviceType) {
            url += `&serviceType=${serviceType}`;
        }
        if (status) {
            url += `&status=${status}`;
        }
        if (date_start) {
            url += `&date_start=${date_start}`;
        }
        if (date_end) {
            url += `&date_end=${date_end}`;
        }

        return this._http.get<{
            services: ServiceRequest[];
            total: number;
            statusCounts?: Record<StatusService, number>;
        }>(url);
    }

    /**
     * Obtém uma lista paginada de serviços pertencentes ao usuário solicitante, com base em parâmetros como limite, página e descrição.
     *
     * @param { number } limit - Número de resultados por página.
     * @param { number } page - Página atual da paginação.
     * @param { string } [description] - Descrição do serviço (opcional).
     * @returns { Observable<ServiceRequestPaginate> } - Observable contendo a resposta paginada dos serviços do usuário.
     */
    getServicesbyUser(
        limit: number,
        page: number,
        description?: string
    ): Observable<ServiceRequestPaginate> {
        let url = `${this.baseUrl}/is_request?limit=${limit}&skip=${page}`;

        if (description) {
            url += `&description=${description.trim()}`;
        }

        return this._http.get<ServiceRequestPaginate>(url);
    }

    /**
     * Conta o número de solicitações de serviço RT.
     *
     * @returns {Observable<number>} - Observable contendo o número de solicitações de serviço RT.
     */
    countRTServices(): Observable<number> {
        return this._http.get<number>(`${this.baseUrl}/count_rt`);
    }

    /**
     * Busca uma solicitação de serviço pelo ID.
     *
     * @param id - ID da solicitação de serviço a ser encontrada.
     * @returns {Observable<ServiceRequestPopulate>} - Observable contendo a solicitação de serviço encontrada.
     */
    getServicesById(id: string): Observable<ServiceRequestPopulate> {
        return this._http.get<ServiceRequestPopulate>(`${this.baseUrl}/${id}`);
    }

    /**
     * Envia uma notificação por email avisando ao requisitante que existe um pedido de documento.
     *
     * @param service_id - ID do serviço.
     * @returns {Observable<ServiceRequest>} - Serviço atualizado.
     */
    newDocumentNotification(service_id: string): Observable<ServiceRequest> {
        return this._http.get<ServiceRequest>(
            `${this.baseUrl}/new-document-notification/${service_id}`
        );
    }

    /**
     * Deleta uma solicitação de serviço pelo ID.
     *
     * @param id - ID da solicitação de serviço a ser deletada.
     * @returns {Observable<ServiceRequestPaginate>} - Observable contendo a solicitação de serviço deletada.
     */
    deleteRequest(id: string): Observable<ServiceRequestPaginate> {
        return this._http.delete<ServiceRequestPaginate>(
            `${this.baseUrl}/${id}`
        );
    }
}
